import React, { useEffect, useState, useContext, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { API_URL } from '../components/config.js';
import axios from 'axios';
import api from '../utils/api';
import './HealthProgramsPage.css';

const HealthProgramsPage = () => {
  const navigate = useNavigate();
  const { currentUser } = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  const [programs, setPrograms] = useState([]);
  const [filteredPrograms, setFilteredPrograms] = useState([]);
  const [userApplications, setUserApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedType, setSelectedType] = useState('All');
  const [selectedTarget, setSelectedTarget] = useState('All');
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [selectedFaq, setSelectedFaq] = useState(null);
  const [programCategories, setProgramCategories] = useState([]);

  // Get dynamic program types based on selected category
  const getProgramTypes = () => {
    const category = searchParams.get('category');
    if (category && programCategories.length > 0) {
      const categoryData = programCategories.find(c => c.key === category);
      if (categoryData && categoryData.programTypes) {
        return ['All', ...categoryData.programTypes];
      }
    }
    // If no category or not found, extract unique types from current programs
    if (programs.length > 0) {
      const uniqueTypes = [...new Set(programs.map(p => p.programType))].filter(Boolean);
      return ['All', ...uniqueTypes];
    }
    return ['All'];
  };

  const programTypes = getProgramTypes();

  const targetPopulations = [
    'All',
    'All Ages',
    'Children',
    'Youth',
    'Adults',
    'Elders',
    'Families',
    'Women',
    'Men'
  ];

  // Fetch program categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await api.get('/api/program-categories?activeOnly=true');
        setProgramCategories(response.data.categories || []);
      } catch (err) {
        console.error('Error fetching program categories:', err);
      }
    };
    fetchCategories();
  }, []);

  const fetchPrograms = useCallback(async () => {
    try {
      setLoading(true);
      const category = searchParams.get('category');
      const url = category 
        ? `${API_URL}/api/health-programs/published?category=${category}`
        : `${API_URL}/api/health-programs/published`;
      const response = await axios.get(url);
      setPrograms(response.data.programs || []);
      setFilteredPrograms(response.data.programs || []);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching health programs:', err);
      setError('Failed to load health programs');
      setLoading(false);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchPrograms();
    if (currentUser) {
      fetchUserApplications();
    }
  }, [currentUser, searchParams, fetchPrograms]);

  // Open specific program from URL parameter
  useEffect(() => {
    const programId = searchParams.get('program');
    if (programId && programs.length > 0) {
      const program = programs.find(p => p._id === programId);
      if (program) {
        setSelectedProgram(program);
      }
    }
  }, [searchParams, programs]);

  const filterPrograms = useCallback(() => {
    let filtered = programs;
    
    if (selectedType !== 'All') {
      filtered = filtered.filter(p => p.programType === selectedType);
    }
    
    if (selectedTarget !== 'All') {
      filtered = filtered.filter(p => p.targetPopulation === selectedTarget);
    }
    
    setFilteredPrograms(filtered);
  }, [programs, selectedType, selectedTarget]);

  useEffect(() => {
    filterPrograms();
  }, [filterPrograms]);

  const fetchUserApplications = async () => {
    try {
      const response = await api.get('/api/health-program-applications?userOnly=true');
      setUserApplications(response.data.applications || []);
    } catch (err) {
      console.error('Error fetching user applications:', err);
    }
  };

  const hasUserApplied = (programId) => {
    return userApplications.some(app => 
      (app.healthProgramId?._id || app.healthProgramId) === programId
    );
  };

  const getUserApplicationStatus = (programId) => {
    const application = userApplications.find(app => 
      (app.healthProgramId?._id || app.healthProgramId) === programId
    );
    return application?.status;
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'approved': return 'status-approved';
      case 'rejected': return 'status-rejected';
      case 'reviewed': return 'status-reviewed';
      case 'needsMoreInfo': return 'status-needs-info';
      case 'waitlisted': return 'status-waitlisted';
      case 'submitted': return 'status-submitted';
      default: return 'status-default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'submitted': return 'Application Submitted';
      case 'reviewed': return 'In Review';
      case 'approved': return 'Approved';
      case 'rejected': return 'Rejected';
      case 'needsMoreInfo': return 'Needs More Info';
      case 'waitlisted': return 'Waitlisted';
      default: return status;
    }
  };



  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="health-programs-container">
        <div className="loading">Loading programs...</div>
      </div>
    );
  }

  const category = searchParams.get('category');
  const categoryInfo = {
    education: { icon: '🎓', title: 'Education Programs', description: 'Scholarships, grants, and educational support' },
    health: { icon: '🏥', title: 'Health Programs & Services', description: 'Access health programs, services, and support available to community members' },
    culture: { icon: '🎨', title: 'Culture & Community Programs', description: 'Cultural programs, language revitalization, and traditional practices' },
    legal: { icon: '⚖️', title: 'Legal Rights & Defence Programs', description: 'Legal services, treaty rights, and advocacy support' }
  };
  const currentCategory = category ? categoryInfo[category] : { icon: '📋', title: 'All Programs & Services', description: 'Browse all available programs and services' };

  return (
    <div className="health-programs-container">
      <div className="programs-header">
        <h1>{currentCategory.icon} {currentCategory.title}</h1>
        <p>{currentCategory.description}</p>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="filter-section">
        <div className="filter-group">
          <label>Program Type:</label>
          <select value={selectedType} onChange={(e) => setSelectedType(e.target.value)}>
            {programTypes.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>For:</label>
          <select value={selectedTarget} onChange={(e) => setSelectedTarget(e.target.value)}>
            {targetPopulations.map(target => (
              <option key={target} value={target}>{target}</option>
            ))}
          </select>
        </div>

        <div className="filter-results">
          Showing {filteredPrograms.length} of {programs.length} programs
        </div>
      </div>

      {selectedProgram ? (
        <div className="program-detail-view">
          <button onClick={() => setSelectedProgram(null)} className="btn-back">
            ← Back to Programs
          </button>

          <div className="program-detail">
            <div className="program-detail-header">
              <div>
                <h2>{selectedProgram.title}</h2>
                <span className="type-badge">{selectedProgram.programType}</span>
                <span className="target-badge">{Array.isArray(selectedProgram.targetPopulation) ? selectedProgram.targetPopulation.join(', ') : selectedProgram.targetPopulation}</span>
                {currentUser && hasUserApplied(selectedProgram._id) && (
                  <span className={`application-status-badge ${getStatusBadgeClass(getUserApplicationStatus(selectedProgram._id))}`}>
                    ✓ {getStatusLabel(getUserApplicationStatus(selectedProgram._id))}
                  </span>
                )}
              </div>
              {selectedProgram.isFeatured && (
                <span className="featured-badge">⭐ Featured Program</span>
              )}
            </div>

            <div className="program-detail-content">
              <section className="detail-section">
                <h3>About This Program</h3>
                <div dangerouslySetInnerHTML={{ __html: selectedProgram.description }} />
              </section>

              {(selectedProgram.focusAreas || selectedProgram.healthFocusAreas) && (selectedProgram.focusAreas || selectedProgram.healthFocusAreas).length > 0 && (
                <section className="detail-section">
                  <h3>Focus Areas</h3>
                  <div className="focus-areas">
                    {(selectedProgram.focusAreas || selectedProgram.healthFocusAreas).map((area, idx) => (
                      <span key={idx} className="focus-badge">{area}</span>
                    ))}
                  </div>
                </section>
              )}

              <section className="detail-section">
                <h3>Eligibility</h3>
                <div dangerouslySetInnerHTML={{ __html: selectedProgram.eligibilityCriteria }} />
              </section>

              <section className="detail-section">
                <h3>How to Apply</h3>
                <div dangerouslySetInnerHTML={{ __html: selectedProgram.applicationProcess }} />
              </section>

              {selectedProgram.requiredDocuments && selectedProgram.requiredDocuments.length > 0 && (
                <section className="detail-section">
                  <h3>Required Documents</h3>
                  <ul className="documents-list">
                    {selectedProgram.requiredDocuments.map((doc, idx) => (
                      <li key={idx}>✓ {doc}</li>
                    ))}
                  </ul>
                </section>
              )}

              {selectedProgram.linkedFaqs && selectedProgram.linkedFaqs.length > 0 && (
                <section className="detail-section">
                  <h3>FAQs</h3>
                  <ul className="documents-list">
                    {selectedProgram.linkedFaqs.map(faq => (
                      <li key={faq._id}>
                        <button
                          className="faq-link-btn"
                          onClick={() => setSelectedFaq(faq)}
                        >
                          ❓ {faq.title}
                        </button>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {(selectedProgram.fundingAmount || selectedProgram.fundingType) && (
                <section className="detail-section">
                  <h3>Funding Information</h3>
                  <div className="funding-info">
                    {selectedProgram.fundingType && (
                      <p><strong>Type:</strong> {selectedProgram.fundingType}</p>
                    )}
                    {selectedProgram.fundingAmount > 0 && (
                      <p><strong>Amount:</strong> ${selectedProgram.fundingAmount.toLocaleString()}</p>
                    )}
                  </div>
                </section>
              )}

              {selectedProgram.contactInfo && (
                <section className="detail-section contact-section">
                  <h3>Contact Information</h3>
                  <div className="contact-grid">
                    {selectedProgram.contactInfo.name && (
                      <div className="contact-item">
                        <strong>Contact:</strong> {selectedProgram.contactInfo.name}
                      </div>
                    )}
                    {selectedProgram.contactInfo.phone && (
                      <div className="contact-item">
                        <strong>Phone:</strong> {selectedProgram.contactInfo.phone}
                      </div>
                    )}
                    {selectedProgram.contactInfo.email && (
                      <div className="contact-item">
                        <strong>Email:</strong> {selectedProgram.contactInfo.email}
                      </div>
                    )}
                    {selectedProgram.contactInfo.location && (
                      <div className="contact-item">
                        <strong>Location:</strong> {selectedProgram.contactInfo.location}
                      </div>
                    )}
                    {selectedProgram.contactInfo.hours && (
                      <div className="contact-item">
                        <strong>Hours:</strong> {selectedProgram.contactInfo.hours}
                      </div>
                    )}
                  </div>
                </section>
              )}

              <div className="program-meta-footer">
                {selectedProgram.applicationDeadline && (
                  <p><strong>Application Deadline:</strong> {formatDate(selectedProgram.applicationDeadline)}</p>
                )}
                {selectedProgram.maxParticipants && (
                  <p><strong>Capacity:</strong> {selectedProgram.currentParticipants || 0}/{selectedProgram.maxParticipants} participants</p>
                )}
                {selectedProgram.startDate && selectedProgram.endDate && !selectedProgram.isOngoing && (
                  <p><strong>Program Dates:</strong> {formatDate(selectedProgram.startDate)} - {formatDate(selectedProgram.endDate)}</p>
                )}
                {selectedProgram.isOngoing && (
                  <p><strong>Program Status:</strong> Ongoing</p>
                )}

              {selectedProgram.sections && selectedProgram.sections.length > 0 && (
                <div className="apply-section">
                  {currentUser && hasUserApplied(selectedProgram._id) ? (
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        const application = userApplications.find(app => 
                          (app.healthProgramId?._id || app.healthProgramId) === selectedProgram._id
                        );
                        if (application) {
                          navigate(`/review-health-application/${application._id}?view=applicant`);
                        }
                      }}
                      className="btn-view-application"
                    >
                      View Your Application
                    </button>
                  ) : (
                    <button 
                      onClick={() => navigate(`/apply-health-program/${selectedProgram._id}`)}
                      className="btn-apply"
                    >
                      Apply for This Program
                    </button>
                  )}
                </div>
              )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="programs-grid">
          {filteredPrograms.length === 0 ? (
            <div className="no-programs">
              <p>No programs available at this time.</p>
              <p>Please check back later or contact us for more information.</p>
            </div>
          ) : (
            filteredPrograms.map((program) => {
              const hasApplied = currentUser && hasUserApplied(program._id);
              const applicationStatus = hasApplied ? getUserApplicationStatus(program._id) : null;
              
              return (
              <div key={program._id} className="program-card" onClick={() => setSelectedProgram(program)}>
                <div className="card-header">
                  <h3>{program.title}</h3>
                  {program.isFeatured && <span className="featured-icon">⭐</span>}
                </div>

                <div className="card-badges">
                  <span className="type-badge">{program.programType}</span>
                  <span className="target-badge">{Array.isArray(program.targetPopulation) ? program.targetPopulation.join(', ') : program.targetPopulation}</span>
                  {hasApplied && applicationStatus && (
                    <span className={`application-status-badge ${getStatusBadgeClass(applicationStatus)}`}>
                      ✓ {getStatusLabel(applicationStatus)}
                    </span>
                  )}
                </div>

                <p className="card-description">
                  {(() => {
                    const tmp = document.createElement('div');
                    tmp.innerHTML = program.description;
                    const plain = (tmp.textContent || tmp.innerText || '').replace(/\s+/g, ' ').trim();
                    return plain.length > 120 ? `${plain.substring(0, 120)}...` : plain;
                  })()}
                </p>

                {(program.focusAreas || program.healthFocusAreas) && (program.focusAreas || program.healthFocusAreas).length > 0 && (
                  <div className="card-focus-areas">
                    {(program.focusAreas || program.healthFocusAreas).slice(0, 3).map((area, idx) => (
                      <span key={idx} className="focus-tag">{area}</span>
                    ))}
                    {(program.focusAreas || program.healthFocusAreas).length > 3 && (
                      <span className="focus-tag">+{(program.focusAreas || program.healthFocusAreas).length - 3} more</span>
                    )}
                  </div>
                )}

                <div className="card-footer">
                  {program.fundingType && (
                    <span className="funding-badge">{program.fundingType}</span>
                  )}
                  <button className="btn-learn-more">Learn More →</button>
                </div>
              </div>
            );
            })
          )}
        </div>
      )}

      {selectedFaq && (
        <div className="modal-overlay" onClick={() => setSelectedFaq(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>❓ {selectedFaq.title}</h3>
              <button className="modal-close" onClick={() => setSelectedFaq(null)}>×</button>
            </div>
            <div className="modal-body" dangerouslySetInnerHTML={{ __html: selectedFaq.content }} />
          </div>
        </div>
      )}
    </div>
  );
};

export default HealthProgramsPage;
